
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tables } from '@/integrations/supabase/types';
import { User, Globe, Image, Search, FileText, Settings, Gamepad } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import BlogImageUpload from './BlogImageUpload';
import RelatedGamesSelector from './RelatedGamesSelector';

type BlogPost = Tables<'blog_posts'>;

interface BlogEditFormProps {
  post: BlogPost | null;
  onSuccess: () => void;
  initialTitle?: string;
  initialContent?: string;
}

const formSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  slug: z.string().min(1, 'Slug is required'),
  content: z.string().optional(),
  author_name: z.string().optional(),
  status: z.enum(['draft', 'published']),
  meta_title: z.string().optional(),
  meta_description: z.string().optional(),
  meta_keywords: z.string().optional(),
  featured_image_url: z.string().url().optional().or(z.literal('')),
});

const BlogEditForm: React.FC<BlogEditFormProps> = ({ post, onSuccess, initialTitle, initialContent }) => {
  const queryClient = useQueryClient();
  const [relatedGameIds, setRelatedGameIds] = useState<string[]>([]);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: '',
      slug: '',
      content: '',
      author_name: 'Admin',
      status: 'published',
      meta_title: '',
      meta_description: '',
      meta_keywords: '',
      featured_image_url: '',
    },
  });

  useEffect(() => {
    const title = initialTitle || post?.title || '';
    const content = initialContent || post?.content || '';
    
    form.reset({
      title: title,
      slug: post?.slug || '',
      content: content,
      author_name: post?.author_name ?? 'Admin',
      status: post?.status as 'draft' | 'published' ?? 'published',
      meta_title: post?.meta_title ?? '',
      meta_description: post?.meta_description ?? '',
      meta_keywords: post?.meta_keywords ?? '',
      featured_image_url: post?.featured_image_url ?? '',
    });

    // Load related games if editing existing post
    if (post?.id) {
      loadRelatedGames(post.id);
    }
  }, [post, initialTitle, initialContent, form]);

  const loadRelatedGames = async (postId: string) => {
    try {
      const { data, error } = await supabase
        .from('blog_post_related_games')
        .select('game_id')
        .eq('blog_post_id', postId);
      
      if (error) throw error;
      setRelatedGameIds(data.map(item => item.game_id));
    } catch (error) {
      console.error('Error loading related games:', error);
    }
  };
  
  const slugify = (text: string) =>
    text
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^\w-]+/g, '')
      .replace(/--+/g, '-');

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    form.setValue('title', e.target.value);
    if (!form.formState.dirtyFields.slug) {
      form.setValue('slug', slugify(e.target.value));
    }
  };

  const saveRelatedGames = async (postId: string, gameIds: string[]) => {
    try {
      // Delete existing relations
      await supabase
        .from('blog_post_related_games')
        .delete()
        .eq('blog_post_id', postId);

      // Insert new relations
      if (gameIds.length > 0) {
        const relations = gameIds.map(gameId => ({
          blog_post_id: postId,
          game_id: gameId
        }));

        const { error } = await supabase
          .from('blog_post_related_games')
          .insert(relations);
        
        if (error) throw error;
      }
    } catch (error) {
      console.error('Error saving related games:', error);
      throw error;
    }
  };

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    const publishedAt = values.status === 'published' && (!post || !post.published_at) ? new Date().toISOString() : post?.published_at;
    
    try {
      let savedPostId: string;

      if (post) {
        const { error } = await supabase.from('blog_posts').update({ ...values, published_at: publishedAt }).eq('id', post.id);
        if (error) throw error;
        savedPostId = post.id;
        toast.success('Blog post updated successfully!');
      } else {
        const insertData = {
          title: values.title,
          slug: values.slug,
          content: values.content,
          author_name: values.author_name,
          status: values.status,
          meta_title: values.meta_title,
          meta_description: values.meta_description,
          meta_keywords: values.meta_keywords,
          featured_image_url: values.featured_image_url,
          published_at: publishedAt,
        };
        const { data, error } = await supabase.from('blog_posts').insert([insertData]).select().single();
        if (error) throw error;
        savedPostId = data.id;
        toast.success('Blog post created successfully!');
      }

      // Save related games
      await saveRelatedGames(savedPostId, relatedGameIds);
      
      queryClient.invalidateQueries({ queryKey: ['blog_posts'] });
      queryClient.invalidateQueries({ queryKey: ['public_blog_posts'] });
      onSuccess();
    } catch (error: any) {
      toast.error('Error saving post:', { description: error.message });
    }
  };

  return (
    <div className="flex flex-col h-[calc(90vh-120px)]">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col h-full">
          <ScrollArea className="flex-1 px-6">
            <div className="space-y-6 py-6">
              
              {/* Main Content Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Blog Content
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Title</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Enter your blog post title..." 
                            {...field} 
                            onChange={handleTitleChange}
                            className="text-lg"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="slug"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>URL Slug</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="post-url-slug" 
                            {...field}
                            className="font-mono text-sm"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="content"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Content</FormLabel>
                        <FormControl>
                          <Textarea 
                            placeholder="Write your blog post content here..." 
                            {...field} 
                            className="min-h-[200px] resize-y"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* Featured Image Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Image className="w-5 h-5" />
                    Featured Image
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <FormField
                    control={form.control}
                    name="featured_image_url"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <BlogImageUpload
                            value={field.value}
                            onChange={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* Related Games Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Gamepad className="w-5 h-5" />
                    Related Games
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <RelatedGamesSelector
                    selectedGameIds={relatedGameIds}
                    onChange={setRelatedGameIds}
                  />
                </CardContent>
              </Card>

              {/* Post Settings Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    Post Settings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="author_name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center gap-2">
                            <User className="w-4 h-4" />
                            Author
                          </FormLabel>
                          <FormControl>
                            <Input placeholder="Author Name" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="status"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center gap-2">
                            <Globe className="w-4 h-4" />
                            Status
                          </FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select status" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="draft">Draft</SelectItem>
                              <SelectItem value="published">Published</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="featured_image_url"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2">
                          <Image className="w-4 h-4" />
                          Featured Image URL
                        </FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="https://example.com/image.jpg" 
                            {...field}
                            className="font-mono text-sm"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* SEO Settings Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Search className="w-5 h-5" />
                    SEO Settings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="meta_title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Meta Title</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="SEO optimized title (recommended: 50-60 characters)" 
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="meta_description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Meta Description</FormLabel>
                        <FormControl>
                          <Textarea 
                            placeholder="Brief description for search engines (recommended: 150-160 characters)" 
                            {...field}
                            className="resize-none"
                            rows={3}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="meta_keywords"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Meta Keywords</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="keyword1, keyword2, keyword3" 
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </div>
          </ScrollArea>

          {/* Action Buttons */}
          <div className="border-t bg-white p-4">
            <div className="flex justify-end gap-3">
              <DialogClose asChild>
                <Button type="button" variant="outline">
                  Cancel
                </Button>
              </DialogClose>
              <Button 
                type="submit" 
                disabled={form.formState.isSubmitting}
              >
                {form.formState.isSubmitting ? 'Saving...' : post ? 'Update Post' : 'Create Post'}
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default BlogEditForm;
